import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  AppBar,
  Toolbar,
  Avatar,
  Card,
  CardContent,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Delete,
  AttachFile,
  Send,
  Logout,
  Person,
  School,
  CalendarToday,
  Description,
  Cancel,
  InsertDriveFile,
  Image as ImageIcon,
  PictureAsPdf,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { createRequest } from "../services/dataServiceWrapper";
import { uploadFiles } from '../services/fileService';
import { getSEMTeamMembers, getERApprovers, getGraphClient, testGraphPermissions } from '../services/graphService';
import { sendApproverNotification } from '../services/emailService';

// Validation schema
const schema = yup.object({
  studentName: yup.string().required('Student name is required'),
  studentNumber: yup.string().required('Student number is required'),
  studentEmail: yup.string().email('Invalid email address').required('Student email is required'),
  award: yup.string().required('Award is required'),
  sem: yup.string().email('Invalid email address').required('SEM is required'),
  unitsAndAssignments: yup.string().required('Units and assignments are required'),
  studentSituation: yup.string().required('Explanation of student situation is required'),
  requestedAdjustments: yup.string().required('Requested adjustments are required'),
  academicDeanResponse: yup.string(),
  approver: yup.string().required('Approver is required'),
});

const ExtensionsRequest = () => {
  const navigate = useNavigate();
  const { accounts, instance } = useMsal();
  const account = accounts[0];
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [semMembers, setSemMembers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      studentName: '',
      studentNumber: '',
      studentEmail: '',
      award: '',
      sem: '',
      unitsAndAssignments: '',
      studentSituation: '',
      requestedAdjustments: '',
      academicDeanResponse: '',
      approver: '',
    },
  });

  useEffect(() => {
    loadADGroups();
  }, [account]);

  const loadADGroups = async () => {
    setLoading(true);
    try {
      const graphClient = getGraphClient(instance, account);
      
      // Test permissions first
      console.log('Testing Graph API permissions...');
      await testGraphPermissions(graphClient);
      
      const [semData, approverData] = await Promise.all([
        getSEMTeamMembers(graphClient),
        getERApprovers(graphClient),
      ]);
      
      setSemMembers(semData);
      setApprovers(approverData);
      
      // Set default approver (Angela Sawyer)
      const defaultApprover = approverData.find(a => 
        a.displayName?.toLowerCase().includes('angela sawyer') ||
        a.email?.toLowerCase().includes('angela.sawyer')
      );
      if (defaultApprover) {
        setValue('approver', defaultApprover.email);
      }
    } catch (error) {
      console.error('Error loading AD groups:', error);
      toast.error('Using cached user lists due to connection issue.');
      // Set mock data
      setSemMembers([
        { id: '1', displayName: 'Sarah Mitchell', email: 'sarah.mitchell@acom.edu.au' },
        { id: '2', displayName: 'James Wilson', email: 'james.wilson@acom.edu.au' },
        { id: '3', displayName: 'Emma Thompson', email: 'emma.thompson@acom.edu.au' },
      ]);
      setApprovers([
        { id: 'angela-default', displayName: 'Angela Sawyer', email: 'asawyer@acom.edu.au' },
        { id: '2', displayName: 'Michael Brown', email: 'michael.brown@acom.edu.au' },
        { id: '3', displayName: 'Jennifer Davis', email: 'jennifer.davis@acom.edu.au' },
      ]);
      setValue('approver', 'asawyer@acom.edu.au');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    // Simulate upload progress
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 20;
      });
    }, 200);

    const newDocuments = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    
    setTimeout(() => {
      setDocuments([...documents, ...newDocuments]);
      setUploadProgress(0);
    }, 1200);
  }, [documents]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 10485760, // 10MB
  });

  const removeDocument = (id) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) return <PictureAsPdf color="error" />;
    if (type.includes('image')) return <ImageIcon color="primary" />;
    return <InsertDriveFile color="action" />;
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      // Create the request
      const newRequest = await createRequest({
        type: 'extensions',
        title: `Extension Request - ${data.studentName} (${data.studentNumber})`,
        ...data,
        submittedBy: account?.email || account?.username || 'current.user@acom.edu.au',
        documents: [], // Empty - will upload separately
        notes: [],
        status: 'pending',
      });

      // Upload files if any (this creates entries in request_documents table)
      if (documents.length > 0) {
        try {
          const filesToUpload = documents.map(doc => doc.file).filter(file => file);
          if (filesToUpload.length > 0) {
            await uploadFiles(newRequest.id, filesToUpload, account?.email || 'unknown');
          }
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          // Continue anyway - request is created
        }
      }

      // Send email notification to approver
      try {
        await sendApproverNotification(newRequest, data.approver);
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      toast.success('Extension request submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {account?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {account?.username || account?.email || 'user@acom.edu.au'}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#ff6b00' }}>
              {account?.name?.charAt(0) || 'U'}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Paper sx={{ p: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                  <School sx={{ mr: 2, fontSize: 32 }} />
                  Extension Request Form
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Complete this form to request an extension for an assignment or assessment
                </Typography>

                <Divider sx={{ mb: 4 }} />

                {/* Student Information Section */}
                <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ mr: 1 }} />
                      Student Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Student Name"
                          {...register('studentName')}
                          error={!!errors.studentName}
                          helperText={errors.studentName?.message}
                          InputLabelProps={{ shrink: true }}
                          placeholder="Enter student's full name"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Student Number"
                          {...register('studentNumber')}
                          error={!!errors.studentNumber}
                          helperText={errors.studentNumber?.message}
                          InputLabelProps={{ shrink: true }}
                          placeholder="Enter student number"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Student Email"
                          type="email"
                          {...register('studentEmail')}
                          error={!!errors.studentEmail}
                          helperText={errors.studentEmail?.message}
                          InputLabelProps={{ shrink: true }}
                          placeholder="student@email.com"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Award and Units Section */}
                <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                      <School sx={{ mr: 1 }} />
                      Academic Details
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Award"
                          {...register('award')}
                          error={!!errors.award}
                          helperText={errors.award?.message}
                          InputLabelProps={{ shrink: true }}
                          placeholder="e.g., Bachelor of Business"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name="sem"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              options={semMembers}
                              getOptionLabel={(option) => 
                                typeof option === 'string' 
                                  ? option 
                                  : `${option.displayName} (${option.email})`
                              }
                              onChange={(event, newValue) => {
                                field.onChange(newValue?.email || '');
                              }}
                              value={semMembers.find(m => m.email === field.value) || null}
                              renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                  <Box>
                                    <Typography variant="body1">{option.displayName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                                  </Box>
                                </Box>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="SEM"
                                  error={!!errors.sem}
                                  helperText={errors.sem?.message}
                                  InputLabelProps={{ shrink: true }}
                                  placeholder="Select SEM contact"
                                />
                              )}
                              fullWidth
                              sx={{
                                '& .MuiAutocomplete-inputRoot': {
                                  minHeight: '56px',
                                },
                                '& .MuiAutocomplete-input': {
                                  width: '100% !important',
                                },
                              }}
                              slotProps={{
                                popper: {
                                  placement: 'bottom-start',
                                  modifiers: [
                                    {
                                      name: 'flip',
                                      enabled: true,
                                    },
                                    {
                                      name: 'preventOverflow',
                                      enabled: true,
                                      options: {
                                        altBoundary: true,
                                        rootBoundary: 'viewport',
                                      },
                                    },
                                  ],
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Unit/s and Assignments for extensions"
                          {...register('unitsAndAssignments')}
                          error={!!errors.unitsAndAssignments}
                          helperText={errors.unitsAndAssignments?.message}
                          InputLabelProps={{ shrink: true }}
                          placeholder="e.g., ACCT101 - Assignment 2, MGMT201 - Final Project"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Student Situation Section */}
                <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Explanation of Student's Situation
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      {...register('studentSituation')}
                      error={!!errors.studentSituation}
                      helperText={errors.studentSituation?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Please provide a detailed explanation of the student's situation requiring an extension..."
                    />
                  </CardContent>
                </Card>

                {/* Requested Adjustments Section */}
                <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Requested Adjustments
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      {...register('requestedAdjustments')}
                      error={!!errors.requestedAdjustments}
                      helperText={errors.requestedAdjustments?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Please specify the adjustments being requested (e.g., extension dates, alternative assessment methods, etc.)"
                    />
                  </CardContent>
                </Card>

                {/* Academic Dean Response Section */}
                <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Associate Academic Dean Response
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      {...register('academicDeanResponse')}
                      error={!!errors.academicDeanResponse}
                      helperText={errors.academicDeanResponse?.message || 'Optional - To be filled by Associate Academic Dean'}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Space for Associate Academic Dean to provide response or comments"
                    />
                  </CardContent>
                </Card>

                {/* Approver Section */}
                <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                      Approval
                    </Typography>
                    <Controller
                      name="approver"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          options={approvers}
                          getOptionLabel={(option) => 
                            typeof option === 'string' 
                              ? option 
                              : `${option.displayName} (${option.email})`
                          }
                          onChange={(event, newValue) => {
                            field.onChange(newValue?.email || '');
                          }}
                          value={approvers.find(a => a.email === field.value) || null}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography variant="body1">{option.displayName}</Typography>
                                <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Approver"
                              error={!!errors.approver}
                              helperText={errors.approver?.message}
                              InputLabelProps={{ shrink: true }}
                              placeholder="Select approver"
                            />
                          )}
                          fullWidth
                          sx={{
                            '& .MuiAutocomplete-inputRoot': {
                              minHeight: '56px',
                            },
                            '& .MuiAutocomplete-input': {
                              width: '100% !important',
                            },
                          }}
                          slotProps={{
                            popper: {
                              placement: 'bottom-start',
                              modifiers: [
                                {
                                  name: 'flip',
                                  enabled: true,
                                },
                                {
                                  name: 'preventOverflow',
                                  enabled: true,
                                  options: {
                                    altBoundary: true,
                                    rootBoundary: 'viewport',
                                  },
                                },
                              ],
                            }
                          }}
                        />
                      )}
                    />
                  </CardContent>
                </Card>

                {/* File Upload Section */}
                <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Supporting Documents
                    </Typography>
                    
                    <Box
                      {...getRootProps()}
                      sx={{
                        border: '2px dashed',
                        borderColor: isDragActive ? 'primary.main' : '#ccc',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: isDragActive ? 'action.hover' : 'transparent',
                        transition: 'all 0.3s',
                        '&:hover': { 
                          bgcolor: 'action.hover',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <input {...getInputProps()} />
                      <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        {isDragActive
                          ? 'Drop files here'
                          : 'Drop files or click to upload'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        PDF, Word, Images (Max 10MB)
                      </Typography>
                    </Box>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption" sx={{ mt: 1 }}>
                          Uploading... {uploadProgress}%
                        </Typography>
                      </Box>
                    )}

                    {documents.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Uploaded Files ({documents.length})
                        </Typography>
                        <List>
                          {documents.map((doc) => (
                            <ListItem
                              key={doc.id}
                              sx={{
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                mb: 1,
                              }}
                            >
                              {getFileIcon(doc.type)}
                              <ListItemText
                                primary={doc.name}
                                secondary={`${(doc.size / 1024 / 1024).toFixed(2)} MB`}
                                sx={{ ml: 2 }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  onClick={() => removeDocument(doc.id)}
                                  color="error"
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Submit Actions */}
                <Box sx={{ mt: 4 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Please ensure all information is accurate before submitting. The approver will be notified via email.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<Cancel />}
                      onClick={() => navigate('/dashboard')}
                      disabled={submitting}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
                      disabled={submitting}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      {submitting ? 'Submitting...' : 'Submit Extension Request'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </form>
          )}
        </motion.div>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 ACOM. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default ExtensionsRequest;